/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  VerticalAlignTop,
  VerticalAlignCenter,
  VerticalAlignBottom,
} from "@mui/icons-material";

const CANVAS_W = 1400;
const CANVAS_H = 840;

// ───── Templates ─────
const TEMPLATES = [
  {
    id: "right-panel-navy",
    label: "Right Panel Navy",
    settings: { overlayStyle: "side-right", overlayColor: "#0a1628", overlayOpacity: 0.8, textColor: "#FFFFFF", accentColor: "#00E5FF", fontSize: 48, textAlign: "center", verticalAlign: "center", logoPosition: "top-right", diagonalSplit: true, showArcs: true, titleCase: "title" },
  },
  {
    id: "left-panel-teal",
    label: "Left Panel Teal",
    settings: { overlayStyle: "side-left", overlayColor: "#0d3b4f", overlayOpacity: 0.8, textColor: "#FFFFFF", accentColor: "#00E5FF", fontSize: 48, textAlign: "left", verticalAlign: "center", logoPosition: "bottom-left", diagonalSplit: true, showArcs: true, titleCase: "title" },
  },
  {
    id: "gradient-bottom-dark",
    label: "Bottom Gradient",
    settings: { overlayStyle: "gradient-bottom", overlayColor: "#000000", overlayOpacity: 0.8, textColor: "#FFFFFF", accentColor: "#FFD700", fontSize: 52, textAlign: "center", verticalAlign: "bottom", logoPosition: "top-right", diagonalSplit: false, showArcs: false, titleCase: "upper" },
  },
  {
    id: "full-overlay-navy",
    label: "Full Navy + Arcs",
    settings: { overlayStyle: "full-dark", overlayColor: "#0a1628", overlayOpacity: 0.7, textColor: "#FFFFFF", accentColor: "#00E5FF", fontSize: 52, textAlign: "center", verticalAlign: "center", logoPosition: "top-left", diagonalSplit: false, showArcs: true, titleCase: "title" },
  },
  {
    id: "right-gradient-teal",
    label: "Right Gradient Teal",
    settings: { overlayStyle: "gradient-right", overlayColor: "#0d3b4f", overlayOpacity: 0.85, textColor: "#FFFFFF", accentColor: "#00E5FF", fontSize: 46, textAlign: "right", verticalAlign: "center", logoPosition: "top-right", diagonalSplit: false, showArcs: true, titleCase: "title" },
  },
  {
    id: "clean-shadow",
    label: "Clean (No Overlay)",
    settings: { overlayStyle: "clean", overlayColor: "#000000", overlayOpacity: 0, textColor: "#FFFFFF", accentColor: "#00E5FF", fontSize: 52, textAlign: "center", verticalAlign: "center", logoPosition: "bottom-right", diagonalSplit: false, showArcs: false, titleCase: "upper" },
  },
];

const OVERLAY_STYLES = [
  { id: "gradient-bottom", label: "Gradient Bottom" },
  { id: "gradient-right", label: "Gradient Right" },
  { id: "full-dark", label: "Full Overlay" },
  { id: "side-left", label: "Panel Left" },
  { id: "side-right", label: "Panel Right" },
  { id: "clean", label: "No Overlay" },
];

const OVERLAY_COLORS = [
  { label: "Black", value: "#000000" },
  { label: "Dark Navy", value: "#0a1628" },
  { label: "Teal Blue", value: "#0d3b4f" },
  { label: "Deep Purple", value: "#1a0a3e" },
  { label: "Dark Green", value: "#0a2e1a" },
  { label: "Burgundy", value: "#3a0a1e" },
];

const TEXT_COLORS = [
  { label: "White", value: "#FFFFFF" },
  { label: "Light Gray", value: "#E0E0E0" },
  { label: "Yellow", value: "#FFD700" },
  { label: "Cyan", value: "#00E5FF" },
  { label: "Black", value: "#000000" },
];

const ACCENT_COLORS = [
  { label: "Cyan", value: "#00E5FF" },
  { label: "Teal", value: "#26C6DA" },
  { label: "Yellow", value: "#FFD700" },
  { label: "Orange", value: "#FF9800" },
  { label: "Pink", value: "#FF4081" },
  { label: "Lime", value: "#AEEA00" },
];

const LOGO_POSITIONS = [
  { id: "top-left", label: "Top Left" },
  { id: "top-right", label: "Top Right" },
  { id: "bottom-left", label: "Bottom Left" },
  { id: "bottom-right", label: "Bottom Right" },
];

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

// Parse accent markup: *word* becomes accent colored
function parseAccentSegments(text) {
  const segments = [];
  const regex = /\*([^*]+)\*/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), accent: false });
    }
    segments.push({ text: match[1], accent: true });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), accent: false });
  }
  return segments.length > 0 ? segments : [{ text, accent: false }];
}

// Wrap text with accent support — returns array of lines, each line is array of segments
function wrapTextWithAccent(ctx, text, maxWidth, font, titleCase) {
  ctx.font = font;
  const caseText = titleCase === "upper" ? text.toUpperCase() : text;
  const segments = parseAccentSegments(caseText);

  // Flatten into words with accent info
  const words = [];
  segments.forEach((seg) => {
    seg.text.split(" ").filter(Boolean).forEach((w) => {
      words.push({ word: w, accent: seg.accent });
    });
  });

  const lines = [];
  let currentLine = [];
  let currentWidth = 0;

  words.forEach((w) => {
    const wordWidth = ctx.measureText(w.word + " ").width;
    if (currentLine.length > 0 && currentWidth + wordWidth > maxWidth) {
      lines.push(currentLine);
      currentLine = [];
      currentWidth = 0;
    }
    currentLine.push(w);
    currentWidth += wordWidth;
  });
  if (currentLine.length > 0) lines.push(currentLine);
  return lines;
}

// Draw a single line with accent coloring
function drawAccentLine(ctx, lineWords, x, y, textColor, accentColor, align, maxWidth) {
  // Measure full line width for alignment
  const fullText = lineWords.map((w) => w.word).join(" ");
  const fullWidth = ctx.measureText(fullText).width;

  let startX;
  if (align === "center") startX = x - fullWidth / 2;
  else if (align === "right") startX = x - fullWidth;
  else startX = x;

  let curX = startX;
  lineWords.forEach((w, i) => {
    ctx.fillStyle = w.accent ? accentColor : textColor;
    const text = i < lineWords.length - 1 ? w.word + " " : w.word;
    ctx.fillText(text, curX, y);
    curX += ctx.measureText(text).width;
  });
}

// Draw decorative concentric arcs
function drawDecorativeArcs(ctx, overlayStyle) {
  let cx, cy;
  if (overlayStyle === "side-right") {
    cx = CANVAS_W * 0.75;
    cy = CANVAS_H * 0.55;
  } else if (overlayStyle === "side-left") {
    cx = CANVAS_W * 0.25;
    cy = CANVAS_H * 0.55;
  } else {
    cx = CANVAS_W * 0.5;
    cy = CANVAS_H * 0.5;
  }

  ctx.save();
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1.5;
  const radii = [120, 180, 250, 330, 420];
  radii.forEach((r) => {
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  });
  ctx.restore();
}

// Draw diagonal split mask
function drawDiagonalSplit(ctx, overlayStyle, overlayColor, overlayOpacity) {
  const rgb = hexToRgb(overlayColor);
  ctx.save();
  ctx.beginPath();
  if (overlayStyle === "side-right") {
    ctx.moveTo(CANVAS_W * 0.4, 0);
    ctx.lineTo(CANVAS_W * 0.55, CANVAS_H);
    ctx.lineTo(CANVAS_W, CANVAS_H);
    ctx.lineTo(CANVAS_W, 0);
  } else if (overlayStyle === "side-left") {
    ctx.moveTo(CANVAS_W * 0.6, 0);
    ctx.lineTo(CANVAS_W * 0.45, CANVAS_H);
    ctx.lineTo(0, CANVAS_H);
    ctx.lineTo(0, 0);
  } else {
    ctx.rect(0, 0, CANVAS_W, CANVAS_H);
  }
  ctx.closePath();
  ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity})`;
  ctx.fill();
  ctx.restore();
}

function drawOnCanvas(canvas, bgImage, logoImage, options) {
  const ctx = canvas.getContext("2d");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;

  // Background
  if (bgImage) {
    ctx.drawImage(bgImage, 0, 0, CANVAS_W, CANVAS_H);
  } else {
    ctx.fillStyle = "#1a1a2e";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }

  const {
    overlayStyle, overlayColor, overlayOpacity,
    titleText, subtitleText, textColor, accentColor, fontSize, subtitleFontSize,
    textAlign, verticalAlign, logoPosition, diagonalSplit, showArcs, titleCase,
  } = options;

  const rgb = hexToRgb(overlayColor);

  // Draw overlay — diagonal or gradient
  if (overlayStyle !== "clean") {
    if (diagonalSplit && (overlayStyle === "side-left" || overlayStyle === "side-right")) {
      drawDiagonalSplit(ctx, overlayStyle, overlayColor, overlayOpacity);
    } else if (overlayStyle === "gradient-bottom") {
      const grad = ctx.createLinearGradient(0, CANVAS_H * 0.25, 0, CANVAS_H);
      grad.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
      grad.addColorStop(0.6, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity * 0.5})`);
      grad.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    } else if (overlayStyle === "gradient-right") {
      const grad = ctx.createLinearGradient(CANVAS_W * 0.3, 0, CANVAS_W, 0);
      grad.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
      grad.addColorStop(0.5, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity * 0.6})`);
      grad.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    } else if (overlayStyle === "full-dark") {
      ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity})`;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    } else if (overlayStyle === "side-left") {
      const grad = ctx.createLinearGradient(0, 0, CANVAS_W * 0.65, 0);
      grad.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity})`);
      grad.addColorStop(0.7, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity * 0.6})`);
      grad.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    } else if (overlayStyle === "side-right") {
      const grad = ctx.createLinearGradient(CANVAS_W * 0.35, 0, CANVAS_W, 0);
      grad.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
      grad.addColorStop(0.3, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity * 0.6})`);
      grad.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},${overlayOpacity})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }
  }

  // Decorative arcs
  if (showArcs) {
    drawDecorativeArcs(ctx, overlayStyle);
  }

  // Text area bounds
  const padding = 70;
  let textAreaLeft, textAreaRight;
  if (overlayStyle === "side-left") {
    textAreaLeft = padding;
    textAreaRight = CANVAS_W * 0.48;
  } else if (overlayStyle === "side-right") {
    textAreaLeft = CANVAS_W * 0.52;
    textAreaRight = CANVAS_W - padding;
  } else {
    textAreaLeft = padding;
    textAreaRight = CANVAS_W - padding;
  }
  const textAreaTop = padding;
  const textAreaBottom = CANVAS_H - padding;
  const maxTextWidth = textAreaRight - textAreaLeft;

  // Draw title with accent support
  if (titleText) {
    const titleFont = `bold ${fontSize}px 'Inter', 'Segoe UI', sans-serif`;
    const titleLines = wrapTextWithAccent(ctx, titleText, maxTextWidth, titleFont, titleCase);
    const titleLineHeight = fontSize * 1.2;
    const titleBlockHeight = titleLines.length * titleLineHeight;

    let subtitleLines = [];
    let subtitleLineHeight = 0;
    let subtitleBlockHeight = 0;
    if (subtitleText) {
      const subFont = `${subtitleFontSize}px 'Inter', 'Segoe UI', sans-serif`;
      ctx.font = subFont;
      const subCaseText = titleCase === "upper" ? subtitleText.toUpperCase() : subtitleText;
      const subWords = subCaseText.split(" ");
      let curLine = subWords[0] || "";
      const subLinesRaw = [];
      for (let i = 1; i < subWords.length; i++) {
        const test = curLine + " " + subWords[i];
        if (ctx.measureText(test).width > maxTextWidth) { subLinesRaw.push(curLine); curLine = subWords[i]; }
        else curLine = test;
      }
      subLinesRaw.push(curLine);
      subtitleLines = subLinesRaw;
      subtitleLineHeight = subtitleFontSize * 1.3;
      subtitleBlockHeight = subtitleLines.length * subtitleLineHeight;
    }

    const gap = subtitleText ? 16 : 0;
    const totalBlockHeight = titleBlockHeight + gap + subtitleBlockHeight;

    let blockStartY;
    if (verticalAlign === "top") blockStartY = textAreaTop;
    else if (verticalAlign === "bottom") blockStartY = textAreaBottom - totalBlockHeight;
    else blockStartY = textAreaTop + (textAreaBottom - textAreaTop - totalBlockHeight) / 2;

    let canvasAlign, textX;
    if (textAlign === "left") { canvasAlign = "left"; textX = textAreaLeft; }
    else if (textAlign === "right") { canvasAlign = "right"; textX = textAreaRight; }
    else { canvasAlign = "center"; textX = textAreaLeft + maxTextWidth / 2; }

    ctx.shadowColor = "rgba(0,0,0,0.5)";
    ctx.shadowBlur = overlayStyle === "clean" ? 12 : 4;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 2;

    // Draw title lines with accent
    ctx.font = titleFont;
    ctx.textAlign = "left"; // We handle alignment manually in drawAccentLine
    titleLines.forEach((lineWords, i) => {
      const y = blockStartY + fontSize + i * titleLineHeight;
      drawAccentLine(ctx, lineWords, textX, y, textColor, accentColor, canvasAlign, maxTextWidth);
    });

    // Draw subtitle
    if (subtitleText && subtitleLines.length > 0) {
      const subStartY = blockStartY + titleBlockHeight + gap;
      ctx.font = `${subtitleFontSize}px 'Inter', 'Segoe UI', sans-serif`;
      ctx.textAlign = canvasAlign;
      ctx.fillStyle = textColor;
      ctx.globalAlpha = 0.85;
      subtitleLines.forEach((line, i) => {
        ctx.fillText(line, textX, subStartY + subtitleFontSize + i * subtitleLineHeight);
      });
      ctx.globalAlpha = 1;
    }

    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  // Draw logo
  if (logoImage) {
    const logoMaxH = 50;
    const logoMaxW = 150;
    const ratio = Math.min(logoMaxW / logoImage.width, logoMaxH / logoImage.height);
    const logoW = logoImage.width * ratio;
    const logoH = logoImage.height * ratio;
    const m = 28;
    let lx, ly;
    if (logoPosition === "top-left") { lx = m; ly = m; }
    else if (logoPosition === "top-right") { lx = CANVAS_W - logoW - m; ly = m; }
    else if (logoPosition === "bottom-left") { lx = m; ly = CANVAS_H - logoH - m; }
    else { lx = CANVAS_W - logoW - m; ly = CANVAS_H - logoH - m; }
    ctx.drawImage(logoImage, lx, ly, logoW, logoH);
  }
}

// ───── Component ─────
export default function FeaturedImageEditor({ open, backgroundSrc, blogTitle, onClose, onDone }) {
  const canvasRef = useRef(null);
  const [bgImage, setBgImage] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [logoSrc, setLogoSrc] = useState(null);

  const [overlayStyle, setOverlayStyle] = useState("side-right");
  const [overlayColor, setOverlayColor] = useState("#0a1628");
  const [overlayOpacity, setOverlayOpacity] = useState(0.75);
  const [titleText, setTitleText] = useState(blogTitle || "");
  const [subtitleText, setSubtitleText] = useState("");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [accentColor, setAccentColor] = useState("#00E5FF");
  const [fontSize, setFontSize] = useState(52);
  const [subtitleFontSize, setSubtitleFontSize] = useState(22);
  const [textAlign, setTextAlign] = useState("center");
  const [verticalAlign, setVerticalAlign] = useState("center");
  const [logoPosition, setLogoPosition] = useState("top-right");
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [diagonalSplit, setDiagonalSplit] = useState(true);
  const [showArcs, setShowArcs] = useState(true);
  const [titleCase, setTitleCase] = useState("title"); // "title" or "upper"

  const clearTemplate = () => setActiveTemplate(null);

  const applyTemplate = (template) => {
    const s = template.settings;
    setOverlayStyle(s.overlayStyle);
    setOverlayColor(s.overlayColor);
    setOverlayOpacity(s.overlayOpacity);
    setTextColor(s.textColor);
    setAccentColor(s.accentColor);
    setFontSize(s.fontSize);
    setTextAlign(s.textAlign);
    setVerticalAlign(s.verticalAlign);
    setLogoPosition(s.logoPosition);
    setDiagonalSplit(s.diagonalSplit);
    setShowArcs(s.showArcs);
    setTitleCase(s.titleCase);
    setActiveTemplate(template.id);
  };

  useEffect(() => {
    if (backgroundSrc) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => setBgImage(img);
      img.src = backgroundSrc;
    }
  }, [backgroundSrc]);

  useEffect(() => {
    if (open && blogTitle) setTitleText(blogTitle);
  }, [open, blogTitle]);

  useEffect(() => {
    if (!canvasRef.current) return;
    drawOnCanvas(canvasRef.current, bgImage, logoImage, {
      overlayStyle, overlayColor, overlayOpacity,
      titleText, subtitleText, textColor, accentColor, fontSize, subtitleFontSize,
      textAlign, verticalAlign, logoPosition, diagonalSplit, showArcs, titleCase,
    });
  }, [bgImage, logoImage, overlayStyle, overlayColor, overlayOpacity, titleText, subtitleText, textColor, accentColor, fontSize, subtitleFontSize, textAlign, verticalAlign, logoPosition, diagonalSplit, showArcs, titleCase]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setLogoSrc(url);
      const img = new Image();
      img.onload = () => setLogoImage(img);
      img.src = url;
    }
    e.target.value = "";
  };

  const handleRemoveLogo = () => { setLogoImage(null); setLogoSrc(null); };

  const handleGenerate = () => {
    if (!canvasRef.current) return;
    onDone(canvasRef.current.toDataURL("image/jpeg", 0.92));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: "12px", maxHeight: "95vh" } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>Featured Image Editor</Typography>
            <Typography variant="caption" color="text.secondary">Customize your featured image (1400 × 840) — wrap words in *asterisks* for accent color</Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 2, overflow: "auto" }}>
        {/* Templates Row */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} gutterBottom display="block">Quick Templates</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {TEMPLATES.map((t) => (
              <Chip key={t.id} label={t.label} size="small" variant={activeTemplate === t.id ? "filled" : "outlined"} color={activeTemplate === t.id ? "primary" : "default"} onClick={() => applyTemplate(t)} sx={{ cursor: "pointer", fontWeight: 500, fontSize: "0.75rem" }} />
            ))}
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Preview */}
          <Grid item xs={12} md={8}>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px", overflow: "hidden", backgroundColor: "#111" }}>
              <canvas ref={canvasRef} style={{ width: "100%", height: "auto", display: "block" }} />
            </Box>
          </Grid>

          {/* Controls */}
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: 480, overflowY: "auto", pr: 0.5 }}>

              {/* Overlay Style */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Overlay Style</Typography>
                <ToggleButtonGroup value={overlayStyle} exclusive onChange={(_, v) => { if (v) { setOverlayStyle(v); clearTemplate(); } }} size="small" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                  {OVERLAY_STYLES.map((s) => (
                    <ToggleButton key={s.id} value={s.id} sx={{ textTransform: "none", fontSize: "0.7rem", px: 1, py: 0.5 }}>{s.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* Diagonal Split + Arcs toggles */}
              <Box display="flex" gap={0} flexWrap="wrap">
                {(overlayStyle === "side-left" || overlayStyle === "side-right") && (
                  <FormControlLabel control={<Switch size="small" checked={diagonalSplit} onChange={(e) => { setDiagonalSplit(e.target.checked); clearTemplate(); }} />} label={<Typography variant="caption">Diagonal split</Typography>} />
                )}
                {overlayStyle !== "clean" && (
                  <FormControlLabel control={<Switch size="small" checked={showArcs} onChange={(e) => { setShowArcs(e.target.checked); clearTemplate(); }} />} label={<Typography variant="caption">Decorative arcs</Typography>} />
                )}
              </Box>

              {/* Overlay Color */}
              {overlayStyle !== "clean" && (
                <Box>
                  <Typography variant="caption" fontWeight={600} gutterBottom display="block">Overlay Color</Typography>
                  <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
                    {OVERLAY_COLORS.map((c) => (
                      <Tooltip key={c.value} title={c.label}>
                        <Box onClick={() => { setOverlayColor(c.value); clearTemplate(); }} sx={{ width: 26, height: 26, borderRadius: "4px", backgroundColor: c.value, border: overlayColor === c.value ? "2px solid #1976d2" : "2px solid #555", cursor: "pointer" }} />
                      </Tooltip>
                    ))}
                    <TextField type="color" value={overlayColor} onChange={(e) => { setOverlayColor(e.target.value); clearTemplate(); }} sx={{ width: 26, height: 26, p: 0, "& input": { p: 0, width: 26, height: 26, cursor: "pointer", border: "none" } }} inputProps={{ style: { padding: 0 } }} />
                  </Box>
                </Box>
              )}

              {/* Overlay Opacity */}
              {overlayStyle !== "clean" && (
                <Box>
                  <Typography variant="caption" fontWeight={600} gutterBottom display="block">Opacity: {Math.round(overlayOpacity * 100)}%</Typography>
                  <Slider value={overlayOpacity} min={0.1} max={0.95} step={0.05} onChange={(_, v) => setOverlayOpacity(v)} size="small" />
                </Box>
              )}

              {/* Title */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Title Text <Typography component="span" variant="caption" color="text.secondary">( *word* = accent )</Typography></Typography>
                <TextField fullWidth size="small" multiline rows={2} value={titleText} onChange={(e) => setTitleText(e.target.value)} placeholder="How *AI-Native Apps* Will Redefine Mobile in 2026" />
              </Box>

              {/* Title Case */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Title Case</Typography>
                <ToggleButtonGroup value={titleCase} exclusive onChange={(_, v) => { if (v) { setTitleCase(v); clearTemplate(); } }} size="small">
                  <ToggleButton value="title" sx={{ textTransform: "none", fontSize: "0.7rem", px: 1.5, py: 0.5 }}>Title Case</ToggleButton>
                  <ToggleButton value="upper" sx={{ textTransform: "none", fontSize: "0.7rem", px: 1.5, py: 0.5 }}>UPPERCASE</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Subtitle */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Subtitle (optional)</Typography>
                <TextField fullWidth size="small" value={subtitleText} onChange={(e) => setSubtitleText(e.target.value)} placeholder="A short tagline or description" />
              </Box>

              {/* Font Sizes */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Title Size: {fontSize}px</Typography>
                <Slider value={fontSize} min={28} max={80} step={2} onChange={(_, v) => setFontSize(v)} size="small" />
              </Box>
              {subtitleText && (
                <Box>
                  <Typography variant="caption" fontWeight={600} gutterBottom display="block">Subtitle Size: {subtitleFontSize}px</Typography>
                  <Slider value={subtitleFontSize} min={14} max={36} step={1} onChange={(_, v) => setSubtitleFontSize(v)} size="small" />
                </Box>
              )}

              {/* Text Color */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Text Color</Typography>
                <Box display="flex" gap={0.5} alignItems="center">
                  {TEXT_COLORS.map((c) => (
                    <Tooltip key={c.value} title={c.label}>
                      <Box onClick={() => setTextColor(c.value)} sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: c.value, border: textColor === c.value ? "3px solid #1976d2" : "2px solid #aaa", cursor: "pointer" }} />
                    </Tooltip>
                  ))}
                  <TextField type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} sx={{ width: 24, height: 24, p: 0, "& input": { p: 0, width: 24, height: 24, cursor: "pointer", border: "none" } }} inputProps={{ style: { padding: 0 } }} />
                </Box>
              </Box>

              {/* Accent Color */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Accent Color <Typography component="span" variant="caption" color="text.secondary">( for *highlighted* words )</Typography></Typography>
                <Box display="flex" gap={0.5} alignItems="center">
                  {ACCENT_COLORS.map((c) => (
                    <Tooltip key={c.value} title={c.label}>
                      <Box onClick={() => { setAccentColor(c.value); clearTemplate(); }} sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: c.value, border: accentColor === c.value ? "3px solid #1976d2" : "2px solid #aaa", cursor: "pointer" }} />
                    </Tooltip>
                  ))}
                  <TextField type="color" value={accentColor} onChange={(e) => { setAccentColor(e.target.value); clearTemplate(); }} sx={{ width: 24, height: 24, p: 0, "& input": { p: 0, width: 24, height: 24, cursor: "pointer", border: "none" } }} inputProps={{ style: { padding: 0 } }} />
                </Box>
              </Box>

              {/* Text Alignment */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Text Alignment</Typography>
                <Box display="flex" gap={1}>
                  <ToggleButtonGroup value={textAlign} exclusive onChange={(_, v) => { if (v) { setTextAlign(v); clearTemplate(); } }} size="small">
                    <ToggleButton value="left"><FormatAlignLeft sx={{ fontSize: 16 }} /></ToggleButton>
                    <ToggleButton value="center"><FormatAlignCenter sx={{ fontSize: 16 }} /></ToggleButton>
                    <ToggleButton value="right"><FormatAlignRight sx={{ fontSize: 16 }} /></ToggleButton>
                  </ToggleButtonGroup>
                  <ToggleButtonGroup value={verticalAlign} exclusive onChange={(_, v) => { if (v) { setVerticalAlign(v); clearTemplate(); } }} size="small">
                    <ToggleButton value="top"><VerticalAlignTop sx={{ fontSize: 16 }} /></ToggleButton>
                    <ToggleButton value="center"><VerticalAlignCenter sx={{ fontSize: 16 }} /></ToggleButton>
                    <ToggleButton value="bottom"><VerticalAlignBottom sx={{ fontSize: 16 }} /></ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>

              {/* Logo */}
              <Box>
                <Typography variant="caption" fontWeight={600} gutterBottom display="block">Logo (optional)</Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                    {logoSrc ? "Change" : "Upload Logo"}
                    <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                  </Button>
                  {logoSrc && <Button size="small" color="error" onClick={handleRemoveLogo} sx={{ textTransform: "none", fontSize: "0.75rem" }}>Remove</Button>}
                </Box>
                {logoSrc && (
                  <Box sx={{ mt: 0.5, p: 0.5, backgroundColor: "#f5f5f5", borderRadius: "4px", display: "inline-block" }}>
                    <img src={logoSrc} alt="Logo" style={{ maxHeight: 30, maxWidth: 100 }} />
                  </Box>
                )}
              </Box>

              {/* Logo Position */}
              {logoSrc && (
                <Box>
                  <Typography variant="caption" fontWeight={600} gutterBottom display="block">Logo Position</Typography>
                  <ToggleButtonGroup value={logoPosition} exclusive onChange={(_, v) => { if (v) { setLogoPosition(v); clearTemplate(); } }} size="small" sx={{ flexWrap: "wrap", gap: 0.5 }}>
                    {LOGO_POSITIONS.map((p) => (
                      <ToggleButton key={p.id} value={p.id} sx={{ textTransform: "none", fontSize: "0.7rem", px: 1, py: 0.5 }}>{p.label}</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleGenerate} sx={{ textTransform: "none", fontWeight: 600 }}>Use This Image</Button>
      </DialogActions>
    </Dialog>
  );
}
